<template>
  <div class="container">
    <!-- 返回按钮 -->
    <div class="header-nav">
      <button class="back-btn" @click="goBack">
        ← 返回激活页面
      </button>
    </div>

    <h1 class="title">biorender高清无水印图片导出</h1>

    <!-- 步骤1: 验证码校验 -->
    <div v-if="currentStep === 1" class="card">
      <div class="step-header">
        <div class="step-indicator">
          <span class="step-number">1</span>
          <span class="step-title">激活码校验</span>
        </div>
      </div>

      <div class="form-group">
        <label class="form-label">请输入激活码</label>
        <input
          v-model="verificationCode"
          class="form-input"
          type="text"
          placeholder="请输入激活码"
          @keyup.enter="verifyCode"
        />
      </div>

      <button
        class="btn btn-primary"
        @click="verifyCode"
        :disabled="loading || !verificationCode.trim()"
      >
        {{ loading ? '验证中...' : '验证' }}
      </button>
    </div>

    <!-- 步骤2: 文件导出配置 -->
    <div v-if="currentStep === 2" class="card">
      <div class="step-header">
        <div class="step-indicator">
          <span class="step-number">2</span>
          <span class="step-title">文件导出配置</span>
        </div>
        <button class="btn-back" @click="goBack">← 返回</button>
      </div>

      <div class="form-group">
        <label class="form-label">图片链接：进入画板右上角"share"，"Copy Link"<span class="required">*</span></label>

        <!-- 示例图片 -->
        <div class="example-image">
          <img :src="require('@/assets/img1.png')" alt="权限设置示例" class="guide-image" />
        </div>

        <input
          v-model="fileUrl"
          :class="urlInputClass"
          type="url"
          placeholder="请输入完整的BioRender文件链接，例如：https://app.biorender.com/..."
          @input="validateFileUrl"
          @blur="validateFileUrl"
        />
        <div v-if="urlValidationError" class="form-error">{{ urlValidationError }}</div>
        <div v-else-if="fileUrl.trim() && !urlValidationError" class="form-success">✓ URL格式正确</div>
        <div class="form-hint">请输入有效的BioRender文件链接</div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label class="form-label">导出格式 <span class="required">*</span></label>
          <select v-model="exportFormat" class="form-select">
            <option value="">请选择格式</option>
            <option value="png">PNG</option>
            <option value="png_transparent">PNG（透明背景）</option>
            <option value="jpg">JPG</option>
            <option value="pptx">PPTX</option>
            <option value="pdf">PDF</option>
          </select>
        </div>

        <div class="form-group">
          <label class="form-label">分辨率 (DPI)</label>
          <select v-model="resolution" class="form-select">
            <option value="72">72 DPI</option>
            <option value="150">150 DPI</option>
            <option value="300">300 DPI (推荐)</option>
            <option value="600">600 DPI</option>
          </select>
        </div>
      </div>

      <button
        class="btn btn-primary"
        @click="startExport"
        :disabled="loading || !canExport"
      >
        {{ loading ? '导出中...' : '开始导出' }}
      </button>
    </div>

    <!-- 步骤3: 导出进度 -->
    <div v-if="currentStep === 3" class="card">
      <div class="step-header">
        <div class="step-indicator">
          <span class="step-number">3</span>
          <span class="step-title">正在导出</span>
        </div>
      </div>

      <div class="export-progress">
        <div class="spinner"></div>
        <p class="progress-text">正在处理您的文件，请稍候...</p>
        <p class="progress-hint">预计需要1-3分钟，请不要关闭页面</p>

        <div class="progress-details">
          <div class="detail-item">
            <span class="detail-label">文件格式:</span>
            <span class="detail-value">{{ exportFormat.toUpperCase() }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">分辨率:</span>
            <span class="detail-value">{{ resolution }} DPI</span>
          </div>
        </div>
      </div>

      <button class="btn btn-secondary" @click="cancelExport">取消导出</button>
    </div>

    <!-- 步骤4: 导出完成 -->
    <div v-if="currentStep === 4" class="card">
      <div class="step-header">
        <div class="step-indicator">
          <span class="step-number">✓</span>
          <span class="step-title">导出完成</span>
        </div>
      </div>

      <div class="export-result">
        <div class="success-icon">🎉</div>
        <p class="result-text">文件导出成功！</p>

        <div class="file-info">
          <div class="info-item">
            <span class="info-label">文件名称:</span>
            <span class="info-value">{{ exportedFileName }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">文件大小:</span>
            <span class="info-value">{{ exportedFileSize }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">导出格式:</span>
            <span class="info-value">{{ actualExportFormat }}</span>
          </div>
        </div>


        <div class="download-section">
          <button
            class="btn btn-download"
            @click="downloadFile"
            :disabled="downloading"
          >
            {{ downloading ? '下载中...' : '立即下载' }}
          </button>
          <p class="download-hint">文件将在24小时后自动删除</p>
        </div>
      </div>

      <button class="btn btn-secondary" @click="startNewExport">导出新文件</button>
    </div>

    <!-- 错误结果弹窗 -->
    <div v-if="showResultModal" class="modal-overlay">
      <div class="modal result-modal">
        <div class="modal-body">
          <div class="result-content">
            <p class="result-text">{{ taskResult }}</p>
            <div v-if="screenshotUrl" class="screenshot-container">
              <img :src="screenshotUrl" alt="错误截图" class="screenshot" />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="confirm-btn" @click="closeResultModal">确定</button>
        </div>
      </div>
    </div>

    <!-- 错误提示 -->
    <div v-if="errorMessage && !showResultModal" class="error-message">
      <div class="error-content">
        <span class="error-icon">⚠️</span>
        <span class="error-text">{{ errorMessage }}</span>
        <button class="error-close" @click="clearError">×</button>
      </div>
      <!-- 如果是权限错误，显示截图 -->
      <div v-if="errorType === 'permission_denied'" class="error-screenshot-section">
        <div class="screenshot-container">
          <img :src="require('@/assets/img.png')" alt="错误截图" class="error-screenshot" />
        </div>
        <p class="screenshot-hint">上图显示了当前的错误状态</p>
      </div>
    </div>

    <!-- 信息提示 -->
    <div v-if="infoMessage" class="info-message">
      <div class="info-content">
        <span class="info-icon">ℹ️</span>
        <span class="info-text">{{ infoMessage }}</span>
        <button class="info-close" @click="clearInfo">×</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ExportPage',
  data() {
    return {
      currentStep: 1,
      loading: false,
      downloading: false,
      verificationCode: '',
      fileUrl: '',
      exportFormat: '',
      resolution: '300',
      errorMessage: '',
      infoMessage: '', // 信息提示
      exportedFileName: '',
      exportedFileSize: '',
      downloadUrl: '',
      pollTimer: null,
      errorType: '',
      errorScreenshot: '',
      shouldShowExecutionScreenshot: false,
      showResultModal: false,
      taskResult: '',
      screenshotUrl: '',
      errorTimer: null, // 添加错误提示自动关闭定时器
      infoTimer: null, // 信息提示自动关闭定时器
      urlValidationError: '' // URL校验错误信息
    }
  },
  computed: {
    canExport() {
      return this.fileUrl.trim() && this.isValidUrl(this.fileUrl) && this.exportFormat && this.resolution && !this.urlValidationError
    },
    actualExportFormat() {
      if (!this.exportedFileName) return ''

      // 从文件名获取扩展名
      const extension = this.exportedFileName.split('.').pop()
      return extension ? extension.toUpperCase() : ''
    },
    // URL校验状态
    urlInputClass() {
      if (!this.fileUrl.trim()) return 'form-input'
      if (this.urlValidationError) return 'form-input error'
      return 'form-input success'
    }
  },
  methods: {
    // URL校验方法
    isValidUrl(string) {
      try {
        const url = new URL(string)
        // 基本URL格式校验
        if (!['http:', 'https:'].includes(url.protocol)) {
          return false
        }
        // 特定于BioRender的校验
        if (!url.hostname.includes('biorender.com')) {
          return false
        }
        return true
      } catch (_) {
        return false
      }
    },

    // 实时校验URL输入
    validateFileUrl() {
      if (!this.fileUrl.trim()) {
        this.urlValidationError = ''
        return
      }

      if (!this.isValidUrl(this.fileUrl)) {
        if (!this.fileUrl.startsWith('http://') && !this.fileUrl.startsWith('https://')) {
          this.urlValidationError = '请输入完整的URL（包含http://或https://）'
        } else if (!this.fileUrl.includes('biorender.com')) {
          this.urlValidationError = '请输入有效的BioRender文件链接'
        } else {
          this.urlValidationError = '请输入有效的URL格式'
        }
      } else {
        this.urlValidationError = ''
      }
    },

    async verifyCode() {
      if (!this.verificationCode.trim()) {
        this.showError('请输入验证码')
        return
      }

      this.loading = true
      try {
        // 调用验证码校验API
        const response = await fetch('/api/v1/verification-codes/verify', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            code: this.verificationCode.trim(),
            email: '', // 导出功能可能不需要邮箱，先传空值
            cardPassword: this.verificationCode.trim(),
            type: 'export' // 标识为导出类型，使用小写
          })
        })

        const result = await response.json()

        if (result.success) {
          this.currentStep = 2
          this.clearError()
          // 存储验证成功的验证码到localStorage
          localStorage.setItem('verificationCode', this.verificationCode.trim())
        } else {
          // 使用后端返回的具体错误信息，如果没有则使用默认提示
          const errorMsg = result.msg || result.message || '验证码错误，请重试'
          this.showError(errorMsg)
        }
      } catch (error) {
        console.error('验证码校验失败:', error)
        this.showError('网络错误，请稍后重试')
      } finally {
        this.loading = false
      }
    },

    async startExport() {
      // 先进行URL校验
      this.validateFileUrl()
      if (this.urlValidationError) {
        this.showError(this.urlValidationError)
        return
      }

      if (!this.canExport) {
        this.showError('请填写完整的导出信息')
        return
      }

      // 从localStorage获取验证过的验证码，如果不存在则提示错误
      const storedVerificationCode = localStorage.getItem('verificationCode')
      if (!storedVerificationCode) {
        this.showError('请先进行验证码校验')
        this.currentStep = 1
        return
      }

      this.loading = true

      try {
        // 调用文件导出API
        const response = await fetch('/api/export/start', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            verificationCode: storedVerificationCode,
            fileUrl: this.fileUrl.trim(),
            format: this.exportFormat,
            resolution: parseInt(this.resolution)
          })
        })

        const result = await response.json()

        if (result.success) {
          // 显示提示信息
          this.showInfo('导出任务已提交成功！即将跳转到任务列表...')

          // 延迟1.5秒后跳转到任务列表页面，携带验证码参数
          setTimeout(() => {
            this.$router.push({
              path: '/export/tasks',
              query: { code: storedVerificationCode }
            })
          }, 1500)
        } else {
          this.showError(result.message || '导出启动失败')
        }
      } catch (error) {
        console.error('导出启动失败:', error)
        this.showError('网络错误，请稍后重试')
      } finally {
        this.loading = false
      }
    },

    startPolling(taskId) {
      this.pollTimer = setInterval(async () => {
        try {
          const response = await fetch(`/api/export/status/${taskId}`)
          const result = await response.json()

          if (result.success) {
            if (result.status === 'completed') {
              // 导出完成
              clearInterval(this.pollTimer)
              this.pollTimer = null

              this.exportedFileName = result.fileName
              this.exportedFileSize = result.fileSize
              this.downloadUrl = result.downloadUrl

              // 根据导出结果决定是否显示截图
              if (result.result === 'File access denied' || result.result === 'Export unavailable') {
                this.shouldShowExecutionScreenshot = true
              } else {
                this.shouldShowExecutionScreenshot = false
              }

              this.currentStep = 4
            } else if (result.status === 'failed') {
              // 导出失败
              clearInterval(this.pollTimer)
              this.pollTimer = null

              // 导出失败，根据错误类型决定显示方式
              if (result.errorType === 'permission_denied') {
                this.taskResult = result.error || result.message || '文件无权限访问，请修改文件权限为可编辑'
                this.screenshotUrl = require('@/assets/img.png')
                this.showResultModal = true
                this.currentStep = 2
              } else {
                this.showError(result.error || result.message || '导出失败')
                this.currentStep = 2
              }
            }
            // 继续轮询 processing 状态
          }
        } catch (error) {
          console.error('轮询状态失败:', error)
          // 网络错误不中断轮询
        }
      }, 3000)

      // 5分钟超时
      setTimeout(() => {
        if (this.pollTimer) {
          clearInterval(this.pollTimer)
          this.pollTimer = null
          this.showError('导出超时，请重试')
          this.currentStep = 2
        }
      }, 300000)
    },

    async downloadFile() {
      if (!this.downloadUrl) {
        this.showError('下载链接不可用')
        return
      }

      this.downloading = true
      try {
        // 构建完整的下载URL
        let fullDownloadUrl = this.downloadUrl
        if (!fullDownloadUrl.startsWith('http')) {
          const baseUrl = window.location.origin
          fullDownloadUrl = baseUrl + this.downloadUrl
        }

        console.log('开始下载文件:', fullDownloadUrl)

        // 使用fetch获取blob数据
        const response = await fetch(fullDownloadUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/octet-stream',
            'Cache-Control': 'no-cache'
          }
        })

        // 检查响应状态
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        // 获取文件名（优先从响应头获取）
        let downloadFileName = this.exportedFileName
        const contentDisposition = response.headers.get('Content-Disposition')
        if (contentDisposition) {
          // 尝试从Content-Disposition头部提取文件名
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
          if (filenameMatch && filenameMatch[1]) {
            downloadFileName = filenameMatch[1].replace(/['"]/g, '')
          }

          // 处理UTF-8编码的文件名
          const utf8FilenameMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/)
          if (utf8FilenameMatch && utf8FilenameMatch[1]) {
            downloadFileName = decodeURIComponent(utf8FilenameMatch[1])
          }
        }

        // 获取blob对象
        const blob = await response.blob()
        console.log('文件blob获取成功, 大小:', blob.size, 'bytes')

        // 创建blob URL
        const blobUrl = window.URL.createObjectURL(blob)

        // 创建下载链接并触发下载
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = downloadFileName || 'download'
        link.style.display = 'none'

        // 添加到DOM并点击
        document.body.appendChild(link)
        link.click()

        // 清理DOM和内存
        document.body.removeChild(link)
        window.URL.revokeObjectURL(blobUrl)

        console.log('下载成功:', downloadFileName)

      } catch (error) {
        console.error('下载失败:', error)

        // 根据错误类型提供更具体的错误信息
        let errorMessage = '下载失败，请重试'
        if (error.message.includes('404')) {
          errorMessage = '文件不存在或已过期'
        } else if (error.message.includes('403')) {
          errorMessage = '没有下载权限'
        } else if (error.message.includes('网络')) {
          errorMessage = '网络错误，请检查连接'
        } else if (error.message.includes('HTTP')) {
          errorMessage = `服务器错误：${error.message}`
        }

        this.showError(errorMessage)
      } finally {
        this.downloading = false
      }
    },

    cancelExport() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
      this.currentStep = 2
      this.loading = false
    },

    startNewExport() {
      this.currentStep = 1
      this.verificationCode = ''
      this.fileUrl = ''
      this.exportFormat = ''
      this.resolution = '300'
      this.exportedFileName = ''
      this.exportedFileSize = ''
      this.downloadUrl = ''
      this.errorType = ''
      this.errorScreenshot = ''
      this.urlValidationError = '' // 清空URL校验错误
      this.clearError()
      // 清除localStorage中的验证代码
      localStorage.removeItem('verificationCode')
    },

    getScreenshotUrl() {
      if (!this.errorScreenshot) return ''
      return `/api/export/download/${this.errorScreenshot}`
    },

    onScreenshotError() {
      console.warn('无法加载错误截图')
    },

    goBack() {
      if (this.currentStep > 1) {
        this.currentStep = this.currentStep - 1
      } else {
        // 如果在第一步，返回到激活页面
        this.$router.push('/')
      }
    },

    showError(message) {
      this.errorMessage = message

      // 清除之前的定时器
      if (this.errorTimer) {
        clearTimeout(this.errorTimer)
      }

      // 5秒后自动关闭错误提示
      this.errorTimer = setTimeout(() => {
        this.clearError()
      }, 5000)
    },

    showInfo(message) {
      this.infoMessage = message

      // 清除之前的定时器
      if (this.infoTimer) {
        clearTimeout(this.infoTimer)
      }

      // 8秒后自动关闭信息提示
      this.infoTimer = setTimeout(() => {
        this.clearInfo()
      }, 8000)
    },

    clearError() {
      this.errorMessage = ''
      this.errorType = ''

      // 清除自动关闭定时器
      if (this.errorTimer) {
        clearTimeout(this.errorTimer)
        this.errorTimer = null
      }
    },

    clearInfo() {
      this.infoMessage = ''

      // 清除自动关闭定时器
      if (this.infoTimer) {
        clearTimeout(this.infoTimer)
        this.infoTimer = null
      }
    },

    closeErrorModal() {
      this.showErrorModal = false
      this.errorMessage = ''
      this.errorType = ''
    },

    closeResultModal() {
      this.showResultModal = false
      this.taskResult = ''
      this.screenshotUrl = ''
    }
  },

  beforeDestroy() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }

    if (this.errorTimer) {
      clearTimeout(this.errorTimer)
      this.errorTimer = null
    }

    if (this.infoTimer) {
      clearTimeout(this.infoTimer)
      this.infoTimer = null
    }
    
    // 组件销毁时清除localStorage中的验证代码
    localStorage.removeItem('verificationCode')
  }
}
</script>

<style scoped>
.container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f7f8fa;
  padding: 20px;
  position: relative;
}

.header-nav {
  position: absolute;
  top: 20px;
  left: 20px;
  width: calc(100% - 40px);
  display: flex;
  justify-content: flex-start;
}

.back-btn {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 8px 16px;
  color: #666;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
}

.back-btn:hover {
  background: #f8f9fa;
  border-color: #2176c7;
  color: #2176c7;
}

.title {
  color: #2176c7;
  font-size: 36px;
  font-weight: bold;
  margin-bottom: 32px;
  text-align: center;
}

.card {
  background: #fff;
  padding: 32px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  width: 100%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
}

.step-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f0f0;
}

.step-indicator {
  display: flex;
  align-items: center;
  gap: 12px;
}

.step-number {
  width: 32px;
  height: 32px;
  background: #2176c7;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 16px;
}

.step-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

.btn-back {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 14px;
  padding: 8px 12px;
  border-radius: 6px;
  transition: background 0.2s;
}

.btn-back:hover {
  background: #f0f0f0;
}

.form-group {
  margin-bottom: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
  font-size: 14px;
}

.required {
  color: #e74c3c;
}

.form-input, .form-select {
  width: 100%;
  height: 44px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 0 16px;
  font-size: 16px;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.form-input:focus, .form-select:focus {
  outline: none;
  border-color: #2176c7;
}

/* URL校验状态样式 */
.form-input.error {
  border-color: #e74c3c;
  background-color: #fdf2f2;
}

.form-input.success {
  border-color: #27ae60;
  background-color: #f0fff4;
}

.form-error {
  margin-top: 4px;
  font-size: 12px;
  color: #e74c3c;
  display: flex;
  align-items: center;
  gap: 4px;
}

.form-error:before {
  content: "⚠️";
  font-size: 14px;
}

.form-success {
  margin-top: 4px;
  font-size: 12px;
  color: #27ae60;
  display: flex;
  align-items: center;
  gap: 4px;
}

.form-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #666;
}

/* 示例图片样式 */
.example-image {
  margin: 10px 0;
  text-align: center;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.guide-image {
  max-width: 100%;
  max-height: 200px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.btn {
  height: 44px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.btn-primary {
  background: #2176c7;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #1a5a9d;
}

.btn-secondary {
  background: #f8f9fa;
  color: #666;
  border: 2px solid #e0e0e0;
}

.btn-secondary:hover:not(:disabled) {
  background: #e9ecef;
}

.btn-download {
  background: #28a745;
  color: white;
}

.btn-download:hover:not(:disabled) {
  background: #218838;
}

.btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}

.export-progress {
  text-align: center;
  padding: 20px 0;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #2176c7;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.progress-text {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.progress-hint {
  font-size: 14px;
  color: #666;
  margin-bottom: 24px;
}

.progress-details, .file-info {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin: 20px 0;
}

.detail-item, .info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e0e0e0;
}

.detail-item:last-child, .info-item:last-child {
  border-bottom: none;
}

.detail-label, .info-label {
  font-weight: 600;
  color: #666;
}

.detail-value, .info-value {
  color: #333;
}

.export-result {
  text-align: center;
}

.export-error {
  text-align: center;
}

.export-error .error-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.export-error .error-text {
  font-size: 18px;
  font-weight: 600;
  color: #e74c3c;
  margin-bottom: 20px;
}

.screenshot-section {
  margin: 20px 0;
  text-align: left;
}

.screenshot-section h4 {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #333;
}

.screenshot-container {
  border: 2px solid #ddd;
  border-radius: 8px;
  padding: 10px;
  background: #f9f9f9;
  max-width: 100%;
  overflow: hidden;
}

.error-screenshot {
  max-width: 100%;
  max-height: 400px;
  width: auto;
  height: auto;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.screenshot-hint {
  font-size: 12px;
  color: #666;
  margin-top: 8px;
  text-align: center;
  font-style: italic;
}

.error-suggestions {
  margin: 20px 0;
  text-align: left;
}

.error-suggestions h4 {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #333;
}

.error-suggestions ul {
  padding-left: 20px;
  margin: 0;
}

.error-suggestions li {
  margin-bottom: 8px;
  color: #555;
  line-height: 1.4;
}

.success-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.result-text {
  font-size: 20px;
  font-weight: 600;
  color: #28a745;
  margin-bottom: 20px;
}

.download-section {
  margin: 24px 0;
}

.download-hint {
  font-size: 12px;
  color: #666;
  margin-top: 8px;
}

.screenshot-container {
  margin-top: 20px;
}

.screenshot-container h4 {
  margin: 0 0 10px 0;
  color: #333;
  font-size: 14px;
}

.screenshot {
  max-width: 100%;
  max-height: 300px;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.error-message {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  max-width: 90%;
}

.error-content {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
  padding: 12px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.error-icon {
  font-size: 18px;
}

.error-text {
  flex: 1;
  font-weight: 500;
}

.error-close {
  background: none;
  border: none;
  color: #721c24;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 信息提示样式 */
.info-message {
  position: fixed;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  max-width: 90%;
}

.info-content {
  background: #d1ecf1;
  border: 1px solid #bee5eb;
  color: #0c5460;
  padding: 12px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.info-icon {
  font-size: 18px;
}

.info-text {
  flex: 1;
  font-weight: 500;
  line-height: 1.5;
}

.info-close {
  background: none;
  border: none;
  color: #0c5460;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .container {
    padding: 16px;
  }

  .title {
    font-size: 28px;
    margin-bottom: 24px;
  }

  .card {
    padding: 24px 20px;
    margin: 0 auto;
  }

  .step-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .step-indicator {
    gap: 8px;
  }

  .step-number {
    width: 28px;
    height: 28px;
    font-size: 14px;
  }

  .step-title {
    font-size: 18px;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .form-input, .form-select {
    height: 40px;
    font-size: 16px; /* 防止iOS缩放 */
  }

  .btn {
    height: 40px;
    font-size: 14px;
  }

  .progress-details, .file-info {
    padding: 12px;
  }

  .detail-item, .info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .error-message {
    left: 16px;
    right: 16px;
    transform: none;
    max-width: none;
  }
}

@media (max-width: 480px) {
  .container {
    padding: 12px;
  }

  .title {
    font-size: 24px;
  }

  .card {
    padding: 20px 16px;
  }

  .form-input, .form-select {
    height: 44px; /* 移动端推荐最小高度 */
  }
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 8px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-body {
  padding: 20px 24px;
  text-align: center;
}

.modal-footer {
  padding: 0 24px 20px;
  text-align: center;
}

.confirm-btn {
  background: #d9534f;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  cursor: pointer;
  font-size: 14px;
}

.confirm-btn:hover:not(:disabled) {
  background: #c9302c;
}

/* 结果弹窗样式 */
.result-modal {
  width: 500px;
  max-width: 95%;
}

.result-content {
  text-align: left;
}

.result-text {
  background: #f0f9ff;
  border: 1px solid #0284c7;
  border-radius: 4px;
  padding: 12px;
  margin: 16px 0;
  color: #0284c7;
  font-weight: 500;
  white-space: pre-wrap;
}
</style>